import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GraduationCap, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import confetti from 'canvas-confetti';
import { useHoneypot } from '@/hooks/useHoneypot';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const LAUNCH_DATE = new Date('2026-02-01T00:00:00Z');
const STORAGE_KEY = 'academy_signup_submitted';
const SESSION_KEY = 'academy_modal_shown_pages';
const DISMISS_DURATION = 2 * 60 * 60 * 1000; // 2 hours in ms

const AcademyLaunchModal: React.FC = () => {
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === 'dark';
  const { honeypotValue, honeypotTimestamp, isBot, HoneypotField } = useHoneypot();
  
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [formData, setFormData] = useState({
    name: '',
    contact: '',
    email: '',
    interest: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Track whether the modal has ever actually been opened
  const hasEverOpenedRef = useRef(false);

  // Detect Netlify SSG prerender
  const isHeadlessPrerender =
    typeof navigator !== 'undefined' && /HeadlessChrome/i.test(navigator.userAgent);

  // Wait for hydration before showing modal
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Check if user already signed up
  useEffect(() => {
    if (!isMounted) return;
    if (isHeadlessPrerender) return;

    // Check for permanent dismissal with 2-hour expiry
    const submittedAt = localStorage.getItem(STORAGE_KEY);
    if (submittedAt) {
      const submitTime = parseInt(submittedAt, 10);
      if (Date.now() - submitTime < DISMISS_DURATION) {
        return; // Still within 2-hour window
      }
      // 2 hours passed, clear the dismissal
      localStorage.removeItem(STORAGE_KEY);
    }
    
    // Check if already shown on this page this session
    const shownPages = sessionStorage.getItem(SESSION_KEY) || '';
    if (shownPages.includes(location.pathname)) {
      return;
    }
    
    // Show popup after delay
    const timer = setTimeout(() => {
      hasEverOpenedRef.current = true;
      setIsOpen(true);
      // Mark this page as shown for this session
      const updatedPages = shownPages 
        ? `${shownPages},${location.pathname}` 
        : location.pathname;
      sessionStorage.setItem(SESSION_KEY, updatedPages);
    }, 3000);
    
    return () => clearTimeout(timer);
  }, [isMounted, isHeadlessPrerender, location.pathname]);

  // Countdown timer
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = LAUNCH_DATE.getTime();
      const difference = target - now;

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        };
      }
      return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    };

    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.contact.trim()) {
      newErrors.contact = 'Contact is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.interest) {
      newErrors.interest = 'Please select an option';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    // Check for bot submission
    if (isBot()) {
      // Silently "succeed" to not reveal bot detection
      toast.success('Thanks for signing up! We\'ll notify you when the Academy launches.');
      setIsOpen(false);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Send email notification with honeypot data for server-side validation
      const { error } = await supabase.functions.invoke('send-academy-signup', {
        body: {
          ...formData,
          website_url: honeypotValue, // Honeypot field
          timestamp: honeypotTimestamp // Form load timestamp
        }
      });
      
      if (error) {
        console.error('Error sending signup:', error);
        toast.error('Something went wrong. Please try again.');
        return;
      }
      
      // Save to localStorage
      // Store timestamp for 2-hour expiry
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
      
      // Fire confetti animation
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      // Fire a second burst for extra celebration
      setTimeout(() => {
        confetti({
          particleCount: 50,
          angle: 60,
          spread: 55,
          origin: { x: 0 }
        });
        confetti({
          particleCount: 50,
          angle: 120,
          spread: 55,
          origin: { x: 1 }
        });
      }, 150);
      
      // Show success toast
      toast.success('Thanks for signing up! We\'ll notify you when the Academy launches.');
      
      // Close modal after confetti
      setTimeout(() => {
        setIsOpen(false);
      }, 1500);
    } catch (err) {
      console.error('Submission error:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const interestOptions = [
    'Marketing strategies',
    'Growing my business',
    'Increasing revenue',
    'Lead generation',
    'Social media mastery',
    'Email marketing',
    'Other'
  ];

  // Theme colors
  const modalBg = isDark ? '#1A1A1A' : '#FFFFFF';
  const headingColor = isDark ? '#FFFFFF' : '#111111';
  const textColor = isDark ? '#AAAAAA' : '#555555';
  const inputBg = isDark ? '#222222' : '#F8F8F8';
  const inputBorder = isDark ? '#333333' : '#E0E0E0';
  const btnBg = isDark ? '#14a3b0' : '#0d7680';
  const btnText = '#FFFFFF';
  const countdownBg = isDark ? '#222222' : '#F0F0F0';
  const badgeBg = isDark ? 'rgba(13, 118, 128, 0.2)' : 'rgba(13, 118, 128, 0.1)';
  const badgeText = '#0d7680';

  const handleOpenChange = useCallback((open: boolean) => {
    setIsOpen(open);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Don't render until mounted (prevents hydration issues on SSG)
  if (!isMounted) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-[90vw] sm:max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6 rounded-xl border-0 [&>button:last-child]:hidden"
        style={{ 
          backgroundColor: modalBg,
          boxShadow: isDark 
            ? '0 25px 50px -12px rgba(0, 0, 0, 0.5)' 
            : '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Custom close button */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 p-1.5 rounded-full transition-colors z-10"
          style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)' }}
          aria-label="Close"
        >
          <X className="w-4 h-4" style={{ color: isDark ? '#AAAAAA' : '#555555' }} />
        </button>

        <DialogHeader className="text-center space-y-3 sm:space-y-4 flex flex-col items-center">
          {/* Badge */}
          <div className="flex justify-center">
            <div 
              className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full"
              style={{ backgroundColor: badgeBg }}
            >
              <GraduationCap className="w-3.5 h-3.5" style={{ color: badgeText }} />
              <span className="text-xs font-medium" style={{ color: badgeText }}>Academy</span>
            </div>
          </div>
          
          <DialogTitle 
            className="text-lg sm:text-xl font-bold tracking-tight text-center w-full"
            style={{ color: headingColor }}
          >
            The Storily Free Academy launches<br />1st Feb 2026
          </DialogTitle>
        </DialogHeader>

        {/* Countdown Timer */}
        <div className="flex justify-center gap-2 sm:gap-3 my-4 sm:my-6">
          {[
            { value: timeLeft.days, label: 'days' },
            { value: timeLeft.hours, label: 'hrs' },
            { value: timeLeft.minutes, label: 'mins' },
            { value: timeLeft.seconds, label: 'secs' }
          ].map((item, index) => (
            <div key={index} className="text-center">
              <div 
                className="w-11 h-11 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center text-base sm:text-xl font-bold overflow-hidden"
                style={{ backgroundColor: countdownBg, color: headingColor }}
              >
                <span 
                  key={item.value} 
                  className="inline-block animate-[countdown-flip_0.3s_ease-out]"
                >
                  {String(item.value).padStart(2, '0')}
                </span>
              </div>
              <span className="text-[9px] sm:text-[10px] mt-0.5 sm:mt-1 block" style={{ color: textColor }}>
                {item.label}
              </span>
            </div>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
          {/* Honeypot field - hidden from users, visible to bots */}
          <HoneypotField />
          <div className="space-y-1 sm:space-y-1.5">
            <Label 
              htmlFor="name" 
              className="text-[11px] sm:text-xs font-medium"
              style={{ color: textColor }}
            >
              Name *
            </Label>
            <Input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="h-8 sm:h-9 text-sm"
              style={{ 
                backgroundColor: inputBg, 
                borderColor: errors.name ? '#EF4444' : inputBorder,
                color: headingColor
              }}
              placeholder="Your name"
            />
            {errors.name && <span className="text-[10px] text-red-500">{errors.name}</span>}
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            <Label 
              htmlFor="contact" 
              className="text-[11px] sm:text-xs font-medium"
              style={{ color: textColor }}
            >
              Contact *
            </Label>
            <Input
              id="contact"
              type="tel"
              value={formData.contact}
              onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
              className="h-8 sm:h-9 text-sm"
              style={{ 
                backgroundColor: inputBg, 
                borderColor: errors.contact ? '#EF4444' : inputBorder,
                color: headingColor
              }}
              placeholder="Phone number"
            />
            {errors.contact && <span className="text-[10px] text-red-500">{errors.contact}</span>}
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            <Label 
              htmlFor="email" 
              className="text-[11px] sm:text-xs font-medium"
              style={{ color: textColor }}
            >
              Email *
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-8 sm:h-9 text-sm"
              style={{ 
                backgroundColor: inputBg, 
                borderColor: errors.email ? '#EF4444' : inputBorder,
                color: headingColor
              }}
              placeholder="your@email.com"
            />
            {errors.email && <span className="text-[10px] text-red-500">{errors.email}</span>}
          </div>

          <div className="space-y-1 sm:space-y-1.5">
            <Label 
              className="text-[11px] sm:text-xs font-medium"
              style={{ color: textColor }}
            >
              What would you like to get most out of the Academy? *
            </Label>
            <Select 
              value={formData.interest} 
              onValueChange={(value) => setFormData({ ...formData, interest: value })}
            >
              <SelectTrigger 
                className="h-8 sm:h-9 text-sm"
                style={{ 
                  backgroundColor: inputBg, 
                  borderColor: errors.interest ? '#EF4444' : inputBorder,
                  color: formData.interest ? headingColor : textColor
                }}
              >
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent style={{ backgroundColor: modalBg }}>
                {interestOptions.map((option) => (
                  <SelectItem 
                    key={option} 
                    value={option}
                    className="text-sm"
                    style={{ color: headingColor }}
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.interest && <span className="text-[10px] text-red-500">{errors.interest}</span>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-2 sm:py-2.5 text-xs font-semibold rounded-lg transition-all duration-200 hover:scale-[1.02] hover:opacity-90 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: btnBg, color: btnText }}
          >
            {isSubmitting ? 'Sending...' : 'Notify Me'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AcademyLaunchModal;
